/**
 * Automatic error reports that could not leave the device (ADR-0141).
 *
 * A small, separate store: the app is offline when it matters most, and an
 * error report is worth keeping, but never worth unbounded storage. At most
 * ten wait; a new one pushes out the oldest. Its own database, so it cannot
 * disturb the typed-note outbox or the scorebook's.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { SupportAutoErrorRequest } from '@good-season/schema';

export const AUTO_OUTBOX_MAX = 10;

interface Entry {
  readonly clientId: string;
  readonly at: number;
  readonly report: SupportAutoErrorRequest;
}

interface AutoDb extends DBSchema {
  queue: { key: string; value: Entry };
}

function open(): Promise<IDBPDatabase<AutoDb>> {
  return openDB<AutoDb>('good-season-help-auto', 1, {
    upgrade(database) {
      database.createObjectStore('queue', { keyPath: 'clientId' });
    },
  });
}

async function withDb<T>(run: (database: IDBPDatabase<AutoDb>) => Promise<T>): Promise<T> {
  const database = await open();
  try {
    return await run(database);
  } finally {
    database.close();
  }
}

/**
 * One read-write transaction, so a burst of errors while offline cannot each see room
 * and all add one: the count, the push-out of the oldest, and the put cannot interleave.
 */
export async function enqueueAuto(report: SupportAutoErrorRequest, at: number): Promise<void> {
  await withDb(async (database) => {
    const tx = database.transaction('queue', 'readwrite');
    const all = await tx.store.getAll();
    const room = AUTO_OUTBOX_MAX - 1;
    const oldest = [...all].sort((x, y) => x.at - y.at).slice(0, Math.max(0, all.length - room));
    for (const entry of oldest) await tx.store.delete(entry.clientId);
    await tx.store.put({ clientId: report.clientId, at, report });
    await tx.done;
  });
}

/** A report older than this is no longer worth sending: the bug was fixed or forgotten. */
export const AUTO_OUTBOX_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export async function pendingAuto(
  now: number = Date.now(),
): Promise<readonly SupportAutoErrorRequest[]> {
  const all = await withDb(async (database) => {
    const tx = database.transaction('queue', 'readwrite');
    const entries = await tx.store.getAll();
    for (const entry of entries) {
      if (now - entry.at > AUTO_OUTBOX_MAX_AGE_MS) await tx.store.delete(entry.clientId);
    }
    await tx.done;
    return entries.filter((entry) => now - entry.at <= AUTO_OUTBOX_MAX_AGE_MS);
  });
  return [...all].sort((a, b) => a.at - b.at).map((entry) => entry.report);
}

export async function removeAuto(clientId: string): Promise<void> {
  await withDb((database) => database.delete('queue', clientId));
}

/** Tests start from an empty queue. */
export async function clearAutoForTests(): Promise<void> {
  await withDb((database) => database.clear('queue'));
}
